// =============================================================================
// MeuDriver — useLgpd (Batch L3)
// Hooks para solicitações de direitos LGPD.
// =============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'

// ─── Types ────────────────────────────────────────────────────────────────────

export type LgpdRequestType = 'export' | 'deletion' | 'correction' | 'portability'
export type LgpdRequestStatus = 'pending' | 'processing' | 'completed' | 'denied'

export interface LgpdRequest {
  id: string
  user_id: string
  request_type: LgpdRequestType
  status: LgpdRequestStatus
  notes: string | null
  requested_at: string
  processed_at: string | null
  processed_by: string | null
}

const LGPD_QUERY_KEY = 'lgpd-requests'

// ─── useMyLgpdRequests ────────────────────────────────────────────────────────

export function useMyLgpdRequests() {
  const { user } = useAuth()

  return useQuery<LgpdRequest[]>({
    queryKey: [LGPD_QUERY_KEY, user?.id ?? ''],
    enabled: Boolean(user?.id),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('lgpd_requests')
        .select('*')
        .eq('user_id', user!.id)
        .order('requested_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as LgpdRequest[]
    },
  })
}

// ─── useCreateLgpdRequest ─────────────────────────────────────────────────────
// Mutation base usada pelas duas funções abaixo e pelo LgpdRequestForm.

export function useCreateLgpdRequest() {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (request_type: LgpdRequestType) => {
      if (!user?.id) throw new Error('Usuário não autenticado.')
      const { error } = await supabase
        .from('lgpd_requests')
        .insert({ user_id: user.id, request_type })
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LGPD_QUERY_KEY, user?.id] })
    },
  })
}

// ─── useRequestDataExport ─────────────────────────────────────────────────────

export function useRequestDataExport() {
  const base = useCreateLgpdRequest()
  return {
    ...base,
    mutate: () => base.mutate('export'),
    mutateAsync: () => base.mutateAsync('export'),
  }
}

// ─── useRequestAccountDeletion ────────────────────────────────────────────────

export function useRequestAccountDeletion() {
  const base = useCreateLgpdRequest()
  return {
    ...base,
    mutate: () => base.mutate('deletion'),
    mutateAsync: () => base.mutateAsync('deletion'),
  }
}
